import { useEffect, useRef, useState } from "react";
import { Camera, RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/view/components/ui/dialog";
import { Button } from "@/view/components/ui/button";

interface CameraCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  fileName?: string;
}

export const CameraCaptureModal = ({
  open,
  onClose,
  onCapture,
  fileName = "foto.jpg",
}: CameraCaptureModalProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [snapshot, setSnapshot] = useState<{
    dataUrl: string;
    blob: Blob;
  } | null>(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async () => {
    setError(null);
    setIsStarting(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Seu navegador não suporta acesso à câmera.");
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
    } catch (err) {
      const e = err as DOMException;
      if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
        setError(
          "Permissão de câmera negada. Habilite o acesso nas configurações do navegador e tente novamente.",
        );
      } else if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError") {
        setError("Nenhuma câmera disponível neste dispositivo.");
      } else {
        setError(e?.message || "Não foi possível acessar a câmera.");
      }
    } finally {
      setIsStarting(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSnapshot(null);
      startCamera();
    } else {
      stopStream();
      setSnapshot(null);
      setError(null);
    }

    return () => {
      stopStream();
    };
  }, [open]);

  const handleTakePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        setSnapshot({ dataUrl, blob });
        stopStream();
      },
      "image/jpeg",
      0.92,
    );
  };

  const handleRetake = async () => {
    setSnapshot(null);
    await startCamera();
  };

  const handleConfirm = () => {
    if (!snapshot) return;
    const safeName = fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")
      ? fileName
      : `${fileName}.jpg`;
    const file = new File([snapshot.blob], safeName, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
    onCapture(file);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogTitle>Tirar foto do documento</DialogTitle>

        <div className="relative w-full overflow-hidden rounded-lg bg-black aspect-[3/4]">
          {snapshot ? (
            <img
              src={snapshot.dataUrl}
              alt="Pré-visualização da foto"
              className="h-full w-full object-contain"
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
            />
          )}

          {(isStarting || error) && !snapshot && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 p-4 text-center text-sm text-white">
              {error ? error : "Iniciando câmera..."}
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          {snapshot ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleRetake}
                className="rounded-full"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Tirar outra
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                className="rounded-full text-white"
              >
                Usar foto
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="rounded-full"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleTakePhoto}
                disabled={!!error || isStarting}
                className="rounded-full text-white"
              >
                <Camera className="mr-2 h-4 w-4" />
                Tirar foto
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
